import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_SECTIONS, EVENT } from "../data/content";

const GROUPS = ["Festival", "Experience", "People", "Partners", "Spotlight"];

export default function Navigation() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => (document.body.style.overflow = "");
    }, [open]);

    return (
        <>
            <motion.header
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    scrolled ? "glass py-3" : "py-5 bg-transparent"
                }`}
                data-testid="main-nav"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between">
                    <a href="#home" className="flex items-center gap-3" data-testid="nav-logo">
                        <img src={LOGO_URL} alt="JAF" className="h-12 md:h-14 w-auto" />
                    </a>

                    <div className="hidden lg:flex items-center gap-8 text-sm uppercase tracking-widest text-zinc-300">
                        <a href="#about" className="hover:text-white transition" data-testid="nav-about">About</a>
                        <a href="#expect" className="hover:text-white transition" data-testid="nav-expect">Experience</a>
                        <a href="#schedule" className="hover:text-white transition" data-testid="nav-schedule">Schedule</a>
                        <a href="#sponsorships" className="hover:text-white transition" data-testid="nav-sponsor">Partner</a>
                        <a href="#awards" className="hover:text-white transition" data-testid="nav-awards">Awards</a>
                    </div>

                    <div className="flex items-center gap-3">
                        <a
                            href="#contact"
                            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#FF6B35] hover:bg-[#FF8957] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 transition-all"
                            data-testid="nav-cta-tickets"
                        >
                            Get Pass <ArrowUpRight size={14} />
                        </a>
                        <button
                            onClick={() => setOpen(true)}
                            className="flex items-center gap-2 glass-light rounded-full px-4 py-3 text-xs uppercase tracking-widest hover:border-[#FF6B35] transition"
                            data-testid="open-menu-btn"
                        >
                            <Menu size={16} /> Menu
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] bg-[#0a0a0a]"
                        data-testid="menu-overlay"
                    >
                        <div className="absolute inset-0 opacity-30 pointer-events-none bg-[radial-gradient(circle_at_top_right,#FF6B35_0%,transparent_45%),radial-gradient(circle_at_bottom_left,#9D4CDD_0%,transparent_45%)]" />

                        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-12 py-6 flex flex-col">
                            <div className="flex items-center justify-between">
                                <img src={LOGO_URL} alt="JAF" className="h-10" />
                                <button
                                    onClick={() => setOpen(false)}
                                    className="flex items-center gap-2 glass-light rounded-full px-4 py-3 text-xs uppercase tracking-widest hover:border-[#FF6B35] transition"
                                    data-testid="close-menu-btn"
                                >
                                    <X size={16} /> Close
                                </button>
                            </div>

                            <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-8 mt-12 md:mt-16 overflow-y-auto">
                                {GROUPS.map((group, gi) => (
                                    <motion.div
                                        key={group}
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.1 + gi * 0.06 }}
                                    >
                                        <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B35] mb-5 font-mono">
                                            {group}
                                        </div>
                                        <ul className="space-y-3">
                                            {NAV_SECTIONS.filter((s) => s.group === group).map((s) => (
                                                <li key={s.id}>
                                                    <a
                                                        href={`#${s.id}`}
                                                        onClick={() => setOpen(false)}
                                                        data-testid={`menu-link-${s.id}`}
                                                        className="group inline-flex items-baseline gap-2 font-display text-2xl md:text-3xl text-white hover:text-[#FF6B35] transition-all"
                                                    >
                                                        {s.label}
                                                        <ArrowUpRight
                                                            size={18}
                                                            className="opacity-0 group-hover:opacity-100 transition -translate-y-1"
                                                        />
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">
                                    {EVENT.dates} · {EVENT.venue}
                                </div>
                                <a
                                    href="#contact"
                                    onClick={() => setOpen(false)}
                                    data-testid="menu-cta-contact"
                                    className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] hover:bg-[#FF8957] text-black font-bold uppercase tracking-wider text-xs px-6 py-3"
                                >
                                    Register Interest <ArrowUpRight size={14} />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
